import { createSlice } from "@reduxjs/toolkit";
import { isTokenExpired, getTokenExpiry } from "../../utils/tokenUtils";
import { getCurrentUser, saveUserToStorage, removeUserFromStorage } from "../../utils/userStorage";
import {
  evaluateSubscriptionAccess,
  normalizeSubscriptionAccessFromApi,
  resolveSubscriptionAccess,
} from "../../utils/subscriptionAccess";

const getStoredApiSubscriptionAccess = (user) =>
  user?.subscriptionAccessFromApi ?? user?.subscriptionAccess ?? null;

const hasSameJsonSnapshot = (left, right) => {
  if (left === right) return true;
  if (!left || !right) return false;
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
};

// Get initial user from storage (active user in current tab).
// Keep expired sessions so TokenExpiryChecker / axios can renew the JWT automatically.
const initialUser = getCurrentUser();
const hasStoredSession = !!(initialUser?.token);

const authSlice = createSlice({
  name: "auth",
  initialState: {
    user: hasStoredSession ? initialUser : null,
    isAuthenticated: hasStoredSession,
    tokenExpiry: hasStoredSession && initialUser?.token ? getTokenExpiry(initialUser.token) : null,
    keepMeLogin: hasStoredSession && initialUser?.keepMeLogin ? initialUser.keepMeLogin : false,
    profileData: hasStoredSession && initialUser?.profileData ? initialUser.profileData : null,
    settingsData: hasStoredSession && initialUser?.settingsData ? initialUser.settingsData : null,
    lastDataRefresh: hasStoredSession && initialUser?.lastDataRefresh ? initialUser.lastDataRefresh : null,
    subscriptionAccess: hasStoredSession
      ? resolveSubscriptionAccess(
          initialUser?.profileData,
          initialUser?.subscriptionAccessFromApi ?? initialUser?.subscriptionAccess
        )
      : evaluateSubscriptionAccess(null),
  },
  reducers: {
    login: (state, action) => {
      const profileData = action.payload.profileData || null;
      const apiSubscriptionAccess = normalizeSubscriptionAccessFromApi(
        action.payload.subscriptionAccessFromApi ?? action.payload.subscriptionAccess
      );
      const subscriptionAccess = resolveSubscriptionAccess(profileData, apiSubscriptionAccess);
      const userWithSubscription = {
        ...action.payload,
        subscriptionAccess,
        subscriptionAccessFromApi: apiSubscriptionAccess,
        subscriptionRestricted: subscriptionAccess.isRestricted,
      };

      state.user = userWithSubscription;
      state.isAuthenticated = true;
      state.keepMeLogin = action.payload.keepMeLogin || false;
      state.profileData = profileData;
      state.settingsData = action.payload.settingsData || null;
      state.lastDataRefresh = action.payload.lastDataRefresh || null;
      state.subscriptionAccess = subscriptionAccess;
      
      // Calculate token expiry if token exists
      if (action.payload.token) {
        const expiry = getTokenExpiry(action.payload.token);
        state.tokenExpiry = expiry || new Date(Date.now() + 60 * 60 * 1000);
      }
      
      // Save user data to localStorage with user-specific key
      saveUserToStorage(userWithSubscription);
    },
    logout: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.tokenExpiry = null;
      state.keepMeLogin = false;
      state.profileData = null;
      state.settingsData = null;
      state.lastDataRefresh = null;
      state.subscriptionAccess = evaluateSubscriptionAccess(null);
      removeUserFromStorage(); // Remove active user from storage
      localStorage.removeItem("keepMeLogin");
      localStorage.removeItem("itemGridPreferences");
    },
    updateToken: (state, action) => {
      if (state.user) {
        const refreshedApiAccess = normalizeSubscriptionAccessFromApi(
          action.payload.subscriptionAccess
        );
        const subscriptionAccess = refreshedApiAccess
          ? resolveSubscriptionAccess(state.profileData, refreshedApiAccess)
          : resolveSubscriptionAccess(state.profileData, state.user.subscriptionAccessFromApi);
        state.user = {
          ...state.user,
          ...action.payload,
          subscriptionAccess,
          subscriptionAccessFromApi:
            refreshedApiAccess ?? state.user.subscriptionAccessFromApi ?? null,
          subscriptionRestricted: subscriptionAccess?.isRestricted ?? false,
        };
        if (subscriptionAccess) state.subscriptionAccess = subscriptionAccess;
        if (action.payload.token) {
          const expiry = getTokenExpiry(action.payload.token);
          state.tokenExpiry = expiry || new Date(Date.now() + 60 * 60 * 1000);
        }
        if (action.payload.keepMeLogin !== undefined) {
          state.keepMeLogin = action.payload.keepMeLogin;
        }
        saveUserToStorage(state.user);
      }
    },
    updateProfileData: (state, action) => {
      state.profileData = action.payload;
      state.subscriptionAccess = resolveSubscriptionAccess(
        action.payload,
        getStoredApiSubscriptionAccess(state.user)
      );
      if (state.user) {
        state.user.profileData = action.payload;
        state.user.subscriptionAccess = state.subscriptionAccess;
        state.user.subscriptionRestricted = state.subscriptionAccess.isRestricted;
        state.user.lastDataRefresh = new Date().toISOString();
        state.lastDataRefresh = new Date().toISOString();
        saveUserToStorage(state.user);
      }
    },
    updateSettingsData: (state, action) => {
      state.settingsData = action.payload;
      if (state.user) {
        state.user.settingsData = action.payload;
        state.user.lastDataRefresh = new Date().toISOString();
        state.lastDataRefresh = new Date().toISOString();
        localStorage.setItem("user", JSON.stringify(state.user));
      }
    },
    updateUserData: (state, action) => {
      const incomingProfile = action.payload.profileData;
      const incomingSettings = action.payload.settingsData;
      const profileChanged =
        incomingProfile != null &&
        !hasSameJsonSnapshot(incomingProfile, state.profileData);
      const settingsChanged =
        incomingSettings != null &&
        !hasSameJsonSnapshot(incomingSettings, state.settingsData);

      if (profileChanged) {
        state.profileData = incomingProfile;
      }
      if (settingsChanged) {
        state.settingsData = incomingSettings;
      }

      state.lastDataRefresh =
        action.payload.lastDataRefresh || new Date().toISOString();

      if (!profileChanged && !settingsChanged) {
        if (state.user) {
          state.user.lastDataRefresh = state.lastDataRefresh;
          saveUserToStorage(state.user);
        }
        return;
      }

      state.subscriptionAccess = resolveSubscriptionAccess(
        state.profileData,
        getStoredApiSubscriptionAccess(state.user)
      );
      if (state.user) {
        state.user.profileData = state.profileData;
        state.user.settingsData = state.settingsData;
        state.user.subscriptionAccess = state.subscriptionAccess;
        state.user.subscriptionRestricted = state.subscriptionAccess.isRestricted;
        state.user.lastDataRefresh = state.lastDataRefresh;
        saveUserToStorage(state.user);
      }
    },
    checkTokenExpiry: (state) => {
      // Expired JWTs are renewed by TokenExpiryChecker / axios (sliding session).
      // Do not force logout here — only logout after a failed refresh.
      if (state.user?.token && isTokenExpired(state.user.token)) {
        return;
      }
    },
  },
});

export const { login, logout, updateToken, updateProfileData, updateSettingsData, updateUserData, checkTokenExpiry } = authSlice.actions;

export const selectSubscriptionAccess = (state) =>
  state.auth.subscriptionAccess ?? evaluateSubscriptionAccess(state.auth.profileData);
export const selectIsSubscriptionRestricted = (state) => selectSubscriptionAccess(state).isRestricted;
export const selectShowExpiryNotice = (state) => selectSubscriptionAccess(state).showExpiryNotice;

export default authSlice.reducer;
